/**
 * presenter-overlay.js — screen-recorder overlay compositor.
 *
 * Feedback #1165 (2026-07-27, Ваня): «без камеры нельзя рисовать и нельзя
 * говорить если нету микрофона» — the only "draw" the tool offered was
 * air-draw (CAMERA hand-tracking), so a user with no camera had no way to
 * annotate; and the camera itself was used only for that gimmick, never for
 * the thing people actually want a camera in a screen recording for: a FACE
 * BUBBLE (Farid's request — the Loom-style circle bottom-right).
 *
 * This module composites, onto one <canvas> that the recorder captures via
 * ScreenRecorder({videoSourceOverride}):
 *     main picture (the screen; or a camera — «Две камеры» in /webcam-recorder)
 *     optionally inset on a «studio frame» backdrop
 *   + optional MOUSE/pen annotations (draw without any camera)
 *   + optional WEBCAM bubble: circle / rounded square / rectangle, in a corner
 *     or wherever it was dragged (2026-10-20: «кружок либо квадрат или
 *     прямоугольник» — the market norm; ours had a circle in four corners)
 *
 * Layouts (2026-10-20):
 *   pip    — the main picture fills the frame, the bubble sits over it
 *   split  — two pictures, top/bottom in a vertical frame, side by side in a
 *            horizontal one (the «пополам» of dual-camera apps)
 *   stack  — automatic when a WIDE main picture (a screen) goes into a TALL
 *            frame (out '9:16'): the screen across the top, the face big
 *            below — the Reels/Shorts shape of «talking over my screen»
 * Output aspect: 'source' (the main picture's own shape, the default) or
 * '9:16' / '16:9' / '1:1' — the canvas is sized from the source's SHORT side,
 * never above 1080.
 *
 * It reuses the exact pipeline air-draw.js proved: screen MediaStream → hidden
 * <video> → per-frame drawImage into a canvas → canvas.captureStream(fps) with
 * the screen's audio tracks re-added. The blade owns the gesture-gated
 * getDisplayMedia; we own the composite.
 *
 * Web-platform honesty: a web page can only annotate OUR composite canvas, not
 * the live OS screen (no browser API paints over other apps). So mouse-draw is
 * most useful when recording a tab/window shown in our preview — same
 * limitation air-draw always had, minus the camera requirement.
 *
 * Public API (window.PresenterOverlay):
 *   init({previewEl, selfviewEl, onstatus, slug})
 *   enableCam() -> Promise<bool>   acquire camera, show live self-view
 *   disableCam()
 *   attachCam(stream|video|canvas, {mirror, corner, size}) / detachCam()
 *   setCorner('br'|'bl'|'tr'|'tl') / setBubbleSize(frac) / setMirror(bool)
 *   setShape('circle'|'square'|'rect') / setPosition(x,y)|null / getPosition()
 *   setLayout('pip'|'split') / setOut('source'|'9:16'|'16:9'|'1:1') / setSwap(bool)
 *   setCamVisible(bool)  — hide the bubble mid-take, the take goes on
 *   setFrame('none'|'light'|'dark'|'violet'|'ocean'|'sunset') / frameEnabled()
 *   setDrawEnabled(bool) / setColor(css) / setBrush(px) / clearDrawing()
 *   camEnabled() / drawEnabled() / isActive() / state()
 *   beginComposite(mainStream, {fps, out, mainFit, keepSource, mainElement, extraAudio}) -> MediaStream
 *   attachDrag(el, {onChange, enabled}) -> detach()  — move the bubble on a preview of the composite
 *                                          (mouse: drag; touch: tap where it should go)
 *   endComposite()
 */
(function () {
    'use strict';
    if (window.PresenterOverlay) return;

    function el(tag, css) { var e = document.createElement(tag); if (css) e.style.cssText = css; return e; }
    function even(n) { n = Math.round(n || 0); if (n % 2) n += 1; return n < 2 ? 2 : n; }
    function clamp(v, a, b) { return v < a ? a : v > b ? b : v; }

    var S = {
        previewEl: null,
        selfviewEl: null,
        onstatus: function () {},
        // camera bubble
        cam: false,
        camStream: null,
        camSource: null,   // stream handed in by attachCam() (not ours to stop)
        selfVideo: null,
        corner: 'br',
        pos: null,         // dragged centre {x,y} in 0..1 of the main picture; null = the corner preset
        sizeFrac: 0.22,    // bubble size as a fraction of the main picture's height
        mirror: true,
        shape: 'circle',   // 'circle' | 'square' | 'rect'
        camVisible: true,
        // layout
        layout: 'pip',     // 'pip' | 'split'
        out: 'source',     // 'source' | '9:16' | '16:9' | '1:1'
        swap: false,       // the bubble's camera becomes the main picture (dual cameras)
        // «красивый кадр» — ключ из FRAMES; 'none' = рамки нет
        frame: 'none',
        // «Область экрана» (24.10.2026): доли {x,y,w,h} кадра ЭКРАНА, которые
        // идут в композит вместо всего экрана; null — весь экран. Камера и
        // элемент-сцена не режутся — только поток экрана из beginComposite.
        crop: null,
        // mouse drawing
        draw: false,
        color: '#ff2d55',
        width: 6,
        strokes: [],       // [{ color, width, pts:[{x,y} normalized 0..1] }]
        current: null,
        // composite
        comp: null,        // { canvas, ctx, video, raf, stream, screenStream, onpd,onpm,onpu, region }
    };

    // A setting that changes the picture repaints on the next tick, not on
    // the next camera frame: a slow or starved camera (a loaded phone) would
    // otherwise leave the old shape on screen — and the old main-picture
    // region under the next tap (measured 21.10.2026: «Half and half» →
    // «Circle», a tap at 20 % placed the bubble at 40 %).
    function touch() { if (S.comp) S.comp.dirty = true; }

    function tel(name, extra) {
        try { if (window.__ae) window.__ae('feature_use', Object.assign({ slug: S.slug || 'screen-recorder', f: name }, extra || {})); } catch (_) {}
    }

    // ── camera bubble ──────────────────────────────────────────────────
    function acquireCam() {
        // Hi-fi is irrelevant for a face bubble; keep it small + cheap. A phone
        // front cam is 'user'; on desktop it's the default webcam.
        return navigator.mediaDevices.getUserMedia({
            video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } },
            audio: false
        });
    }

    function showSelfView() {
        if (!S.selfviewEl || !S.selfVideo) return;
        S.selfviewEl.innerHTML = '';
        S.selfVideo.style.cssText = 'width:100%;height:100%;object-fit:cover;display:block;transform:' + (S.mirror ? 'scaleX(-1)' : 'none');
        S.selfviewEl.appendChild(S.selfVideo);
        S.selfviewEl.hidden = false;
    }
    function hideSelfView() {
        if (S.selfviewEl) { S.selfviewEl.innerHTML = ''; S.selfviewEl.hidden = true; }
    }

    // Size of a picture source: a VideoFrame (a track read directly), a
    // <video> (own camera / screen element) or a <canvas> (webcam-recorder's
    // processor canvas, which already carries the user's blur/crop).
    function srcDims(src) {
        if (!src) return null;
        if (typeof src.codedWidth === 'number') return { w: src.displayWidth || src.codedWidth, h: src.displayHeight || src.codedHeight };
        if (src.tagName === 'CANVAS') return { w: src.width, h: src.height };
        if (src.tagName === 'VIDEO') {
            if (src.readyState < 2 || !src.videoWidth) return null;
            return { w: src.videoWidth, h: src.videoHeight };
        }
        return null;
    }

    // Draw `src` into box b: fit 'cover' (crop to fill), 'contain' (whole
    // picture, letterboxed) or 'fill' (same shape — exact). `mirror` flips it
    // around the box's own vertical axis. A live camera <video> goes through
    // window.__drawCameraFrame: the fallback where the track has no
    // MediaStreamTrackProcessor (Safari, Firefox) is a camera the system can
    // interrupt mid-recording — WebKit throws from drawImage then, the
    // primitive answers false. Returns false when nothing was drawn.
    // Размер источника с учётом области экрана (crop — доли или null).
    function cropDims(dm, crop) {
        if (!dm || !crop) return dm;
        return { w: dm.w * crop.w, h: dm.h * crop.h, ox: dm.w * crop.x, oy: dm.h * crop.y };
    }
    function putSource(ctx, src, b, fit, mirror, liveCamEl, crop) {
        var dm = cropDims(srcDims(src), crop);
        if (!dm || !dm.w || !dm.h) return false;
        var sx = 0, sy = 0, sw = dm.w, sh = dm.h, dx = b.x, dy = b.y, dw = b.w, dh = b.h;
        if (fit === 'cover') {
            var sc = Math.max(b.w / dm.w, b.h / dm.h);
            sw = b.w / sc; sh = b.h / sc; sx = (dm.w - sw) / 2; sy = (dm.h - sh) / 2;
        } else if (fit === 'contain') {
            var sc2 = Math.min(b.w / dm.w, b.h / dm.h);
            dw = dm.w * sc2; dh = dm.h * sc2; dx = b.x + (b.w - dw) / 2; dy = b.y + (b.h - dh) / 2;
        }
        if (dm.ox || dm.oy) { sx += dm.ox || 0; sy += dm.oy || 0; }
        var ok = true;
        ctx.save();
        if (mirror) { ctx.translate(dx * 2 + dw, 0); ctx.scale(-1, 1); }
        try {
            if (liveCamEl && typeof window.__drawCameraFrame === 'function') ok = window.__drawCameraFrame(ctx, src, sx, sy, sw, sh, dx, dy, dw, dh);
            else ctx.drawImage(src, sx, sy, sw, sh, dx, dy, dw, dh);
        } catch (_) { ok = false; }
        ctx.restore();
        return ok !== false;
    }
    function isLiveCamEl(src) { return !!(src && src.tagName === 'VIDEO' && src.srcObject); }

    // The bubble's box inside the main picture R (canvas px).
    // Size: a fraction of R's SHORT side (its height for a screen, as before;
    // its width for a vertical frame — of the height a phone-shaped 9:16 took
    // half the width at the default), at least 96 px, at most 60 % of the
    // short side. A rectangle is the camera's own shape: 4:3 across a wide
    // picture, 3:4 down a tall one.
    // Position: the dragged centre (S.pos, fractions of R) or the corner
    // preset; always kept whole inside R.
    // `g` — прямоугольник экрана внутри подложки: углы отсчитываются от НЕГО,
    // иначе при включённой рамке кружок наполовину сползает на подложку.
    function bubbleGeom(R) {
        var d = clamp(Math.round(Math.min(R.w, R.h) * S.sizeFrac), 96, Math.max(96, Math.round(Math.min(R.w, R.h) * 0.6)));
        var bw = d, bh = d;
        if (S.shape === 'rect') {
            if (R.w >= R.h) { bw = Math.round(d * 4 / 3); } else { bh = Math.round(d * 4 / 3); }
            var k = Math.min(1, (R.w * 0.8) / bw, (R.h * 0.8) / bh);
            bw = Math.round(bw * k); bh = Math.round(bh * k);
        }
        var m = Math.round(Math.min(R.w, R.h) * 0.03); // margin
        var cx, cy;
        if (S.pos) { cx = R.x + S.pos.x * R.w; cy = R.y + S.pos.y * R.h; }
        else if (S.corner === 'bl') { cx = R.x + m + bw / 2; cy = R.y + R.h - m - bh / 2; }
        else if (S.corner === 'tr') { cx = R.x + R.w - m - bw / 2; cy = R.y + m + bh / 2; }
        else if (S.corner === 'tl') { cx = R.x + m + bw / 2; cy = R.y + m + bh / 2; }
        else { cx = R.x + R.w - m - bw / 2; cy = R.y + R.h - m - bh / 2; }
        cx = clamp(cx, R.x + bw / 2, R.x + R.w - bw / 2);
        cy = clamp(cy, R.y + bh / 2, R.y + R.h - bh / 2);
        var r = S.shape === 'circle' ? Math.min(bw, bh) / 2 : Math.round(Math.min(bw, bh) * 0.16);
        return { x: Math.round(cx - bw / 2), y: Math.round(cy - bh / 2), w: bw, h: bh, r: r, shape: S.shape };
    }

    function shapePath(ctx, b) {
        if (b.shape === 'circle') {
            ctx.beginPath();
            ctx.arc(b.x + b.w / 2, b.y + b.h / 2, Math.min(b.w, b.h) / 2, 0, Math.PI * 2);
            ctx.closePath();
        } else {
            roundRectPath(ctx, b.x, b.y, b.w, b.h, b.r);
        }
    }

    // The camera inside its box: clipped to the shape, cover-fit, with a ring.
    // No camera frame this tick: a dark box, not a flash of what is behind it.
    function drawBubbleBox(ctx, src, b, mirror, liveCam, crop) {
        ctx.save();
        shapePath(ctx, b);
        ctx.clip();
        var drawn = putSource(ctx, src, b, 'cover', mirror, liveCam, crop);
        if (!drawn) { ctx.fillStyle = '#111'; ctx.fillRect(b.x, b.y, b.w, b.h); }
        ctx.restore();
        ctx.save();
        shapePath(ctx, b);
        ctx.lineWidth = Math.max(2, Math.round(Math.min(b.w, b.h) * 0.02));
        ctx.strokeStyle = 'rgba(255,255,255,.85)';
        ctx.stroke();
        ctx.restore();
    }

    // ── «красивый кадр» ────────────────────────────────────────────────
    //
    // Экран кладётся на подложку с полями, скруглением и тенью. Это то, чем
    // живёт весь класс «cinematic screen recorder» 2026 года — Screen Studio,
    // FocuSee, Tella, Cursorful, — и то, чего нет ни у одного БРАУЗЕРНОГО
    // рекордера: они отдают сырой кадр захвата.
    //
    // Нам это не стоит ничего: цикл уже рисует кадр экрана в холст, чтобы
    // положить сверху кружок камеры и аннотации. Подложка — та же одна
    // операция drawImage, только в меньший прямоугольник, плюс заливка под
    // ним. Ни второго прохода, ни перекодирования.
    //
    // ⚠️ Размер ВЫХОДА не меняется: экран вписывается ВНУТРЬ него. Иначе
    // 1920×1080 превратился бы в 2100×1200, кодировщик получил бы
    // нестандартный кадр, а у пользователя вырос бы файл — платить
    // разрешением за оформление никто не просил.
    var FRAMES = {
        none:   null,
        light:  { a: '#eef2f9', b: '#dfe6f2' },
        dark:   { a: '#14161d', b: '#242836' },
        violet: { a: '#6d5efc', b: '#c05cf6' },
        ocean:  { a: '#0ea5e9', b: '#2563eb' },
        sunset: { a: '#f97316', b: '#e11d48' }
    };

    // Геометрия кадра внутри подложки. Доля поля подобрана так, чтобы текст
    // на записанном экране оставался читаемым: 7% с каждой стороны —
    // 1920→1651 px по ширине, то есть масштаб 0.86. Ниже 0.8 мелкий шрифт
    // начинает разваливаться, и оформление съедает содержание.
    function frameGeom(W, H) {
        var pad = Math.round(Math.min(W, H) * 0.07);
        var iw = W - pad * 2, ih = H - pad * 2;
        return { x: pad, y: pad, w: iw, h: ih, r: Math.round(Math.min(iw, ih) * 0.022) };
    }

    function roundRectPath(ctx, x, y, w, h, r) {
        r = Math.min(r, w / 2, h / 2);
        ctx.beginPath();
        // roundRect есть везде, где есть WebCodecs, но композитор работает и
        // на rAF-лейне (Firefox) — там сборка может быть старше.
        if (ctx.roundRect) { ctx.roundRect(x, y, w, h, r); return; }
        ctx.moveTo(x + r, y);
        ctx.arcTo(x + w, y, x + w, y + h, r);
        ctx.arcTo(x + w, y + h, x, y + h, r);
        ctx.arcTo(x, y + h, x, y, r);
        ctx.arcTo(x, y, x + w, y, r);
        ctx.closePath();
    }

    function fillGradient(ctx, W, H, f) {
        var grad = ctx.createLinearGradient(0, 0, W, H);
        grad.addColorStop(0, f.a);
        grad.addColorStop(1, f.b);
        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, W, H);
    }

    // Рисует подложку и возвращает прямоугольник, в который лечь экрану.
    // null — рамка выключена, экран занимает кадр целиком (прежнее поведение).
    function paintBackdrop(ctx, W, H) {
        var f = FRAMES[S.frame];
        if (!f) return null;
        var g = frameGeom(W, H);
        ctx.save();
        fillGradient(ctx, W, H, f);
        // Тень под «окном» — она и создаёт ощущение, что экран лежит НА
        // подложке, а не вырезан в ней.
        ctx.shadowColor = 'rgba(0,0,0,.42)';
        ctx.shadowBlur = Math.round(Math.min(W, H) * 0.035);
        ctx.shadowOffsetY = Math.round(Math.min(W, H) * 0.012);
        ctx.fillStyle = '#000';
        roundRectPath(ctx, g.x, g.y, g.w, g.h, g.r);
        ctx.fill();
        ctx.restore();
        return g;
    }

    // The main picture into its region, rounded when it lies on a backdrop.
    function drawMain(ctx, src, R, fit, mirror, liveCam, crop) {
        if (R.r) { ctx.save(); roundRectPath(ctx, R.x, R.y, R.w, R.h, R.r); ctx.clip(); }
        if (fit === 'contain') { ctx.fillStyle = '#05070d'; ctx.fillRect(R.x, R.y, R.w, R.h); }
        putSource(ctx, src, R, fit, mirror, liveCam, crop);
        if (R.r) ctx.restore();
    }

    // One frame of the composite. A = the main picture, B = the bubble's
    // camera. Returns the main picture's region (for the bubble's drag).
    function paintComposite(ctx, W, H, mainSrc, camSrc, mainFit) {
        var A = mainSrc, B = S.cam ? camSrc : null;
        var aMirror = false, bMirror = S.mirror;
        if (S.swap && B) { A = camSrc; B = mainSrc; aMirror = S.mirror; bMirror = false; }
        var aLive = isLiveCamEl(A), bLive = isLiveCamEl(B);
        var showB = !!B && S.camVisible;
        // область экрана режет только экран — где бы он ни оказался после «поменять»
        var screenCrop = (S.comp && S.comp.screenStream) ? S.crop : null;
        var aCrop = A === mainSrc ? screenCrop : null, bCrop = B === mainSrc ? screenCrop : null;
        var aDims = cropDims(srcDims(A), aCrop);
        var tall = H > W;
        var region;

        if (S.layout === 'split' && showB) {
            ctx.fillStyle = '#000';
            ctx.fillRect(0, 0, W, H);
            var gap = Math.max(2, Math.round(Math.min(W, H) * 0.006));
            var r1, r2;
            if (tall) {
                var hh = Math.round(H / 2);
                r1 = { x: 0, y: 0, w: W, h: hh - gap, r: 0 }; r2 = { x: 0, y: hh + gap, w: W, h: H - hh - gap, r: 0 };
            } else {
                var hw = Math.round(W / 2);
                r1 = { x: 0, y: 0, w: hw - gap, h: H, r: 0 }; r2 = { x: hw + gap, y: 0, w: W - hw - gap, h: H, r: 0 };
            }
            putSource(ctx, A, r1, 'cover', aMirror, aLive, aCrop);
            putSource(ctx, B, r2, 'cover', bMirror, bLive, bCrop);
            return r1;
        }

        // A wide picture in a tall frame: the screen across the top, the face
        // big below — fitted whole, never cropped (text on a screen must stay).
        if (tall && S.out !== 'source' && aDims && aDims.w > aDims.h && mainFit !== 'cover') {
            fillGradient(ctx, W, H, FRAMES[S.frame] || FRAMES.dark);
            var pad = Math.round(W * 0.04);
            var rw = W - pad * 2;
            var rh = Math.round(rw * aDims.h / aDims.w);
            var ry = showB ? Math.round(H * 0.08) : Math.round((H - rh) / 2);
            region = { x: pad, y: ry, w: rw, h: rh, r: Math.round(rw * 0.025) };
            drawMain(ctx, A, region, 'fill', aMirror, aLive, aCrop);
            if (S.draw) renderStrokes(ctx, W, H);
            if (showB) {
                var top = ry + rh + Math.round(H * 0.035);
                var below = { x: pad, y: top, w: rw, h: H - top - Math.round(H * 0.06) };
                var b;
                if (S.shape === 'rect') {
                    b = { x: below.x, y: below.y, w: below.w, h: below.h, r: Math.round(Math.min(below.w, below.h) * 0.06), shape: 'rect' };
                } else {
                    var d = Math.round(Math.min(below.w * 0.74, below.h));
                    b = { x: Math.round(W / 2 - d / 2), y: below.y + Math.round((below.h - d) / 2), w: d, h: d, r: S.shape === 'circle' ? d / 2 : Math.round(d * 0.16), shape: S.shape };
                }
                drawBubbleBox(ctx, B, b, bMirror, bLive, bCrop);
            }
            return region;
        }

        // pip — the main picture fills the frame (or lies on the backdrop),
        // the bubble over it.
        var g = paintBackdrop(ctx, W, H);
        region = g ? { x: g.x, y: g.y, w: g.w, h: g.h, r: g.r } : { x: 0, y: 0, w: W, h: H, r: 0 };
        var fit = 'fill';
        if (aDims) {
            var same = Math.abs(aDims.w / aDims.h - region.w / region.h) < 0.01;
            // A screen of another shape is shown WHOLE (letterboxed) — it is
            // text; a camera fills the frame. Before 2026-10-20 a switched-to
            // window of another shape was stretched here.
            if (!same) fit = mainFit === 'cover' ? 'cover' : 'contain';
        }
        if (!g && fit === 'fill') putSource(ctx, A, region, 'fill', aMirror, aLive, aCrop);
        else drawMain(ctx, A, region, fit, aMirror, aLive, aCrop);
        if (S.draw) renderStrokes(ctx, W, H);
        if (showB) drawBubbleBox(ctx, B, bubbleGeom(region), bMirror, bLive, bCrop);
        return region;
    }

    // ── mouse drawing ──────────────────────────────────────────────────
    // ⚠️ Штрихи НЕ пересчитываются под рамку — и это осознанно. Пользователь
    // рисует по ТОМУ ЖЕ холсту, который видит (композит целиком, вместе с
    // подложкой), и `ptrToNorm` даёт доли от него. Отобразить те же доли в
    // прямоугольник экрана значило бы увести линию из-под курсора: точка,
    // поставленная на подложке, вообще не имеет соответствия внутри экрана.
    // Правильная система координат здесь — весь кадр.
    function renderStrokes(ctx, W, H) {
        ctx.save();
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        var all = S.strokes.slice();
        if (S.current) all.push(S.current);
        for (var i = 0; i < all.length; i++) {
            var st = all[i], p = st.pts;
            if (!p.length) continue;
            ctx.strokeStyle = st.color;
            ctx.lineWidth = st.width;
            // soft glow
            ctx.shadowColor = st.color;
            ctx.shadowBlur = st.width * 1.1;
            ctx.beginPath();
            ctx.moveTo(p[0].x * W, p[0].y * H);
            if (p.length === 1) { ctx.lineTo(p[0].x * W + 0.1, p[0].y * H + 0.1); }
            else { for (var j = 1; j < p.length; j++) ctx.lineTo(p[j].x * W, p[j].y * H); }
            ctx.stroke();
        }
        ctx.restore();
    }

    // Map a pointer event on a preview of the composite (object-fit:contain)
    // to normalized [0..1] composite coords, accounting for the letterbox.
    function ptrToNorm(target, W, H, ev) {
        var r = target.getBoundingClientRect();
        if (!r.width || !r.height) return null;
        var scale = Math.min(r.width / W, r.height / H);
        var dw = W * scale, dh = H * scale;
        var ox = (r.width - dw) / 2, oy = (r.height - dh) / 2;
        var px = (ev.clientX - r.left - ox) / scale;
        var py = (ev.clientY - r.top - oy) / scale;
        if (px < 0 || py < 0 || px > W || py > H) return null; // in letterbox
        return { x: px / W, y: py / H };
    }

    // The composite's size. 'source' keeps the main picture's own shape (the
    // pre-2026-10-20 behaviour); other shapes are built from the source's SHORT
    // side, never above 1080 and never below 360.
    function outSize(w, h, out) {
        if (!out || out === 'source') return { w: even(w), h: even(h) };
        var sh = clamp(Math.min(w, h, 1080), 360, 1080);
        if (out === '9:16') return { w: even(sh), h: even(sh * 16 / 9) };
        if (out === '1:1') return { w: even(sh), h: even(sh) };
        return { w: even(sh * 16 / 9), h: even(sh) };
    }

    // ── public ─────────────────────────────────────────────────────────
    window.PresenterOverlay = {
        init: function (opts) {
            opts = opts || {};
            S.previewEl = opts.previewEl || null;
            S.selfviewEl = opts.selfviewEl || null;
            S.slug = opts.slug || S.slug || 'screen-recorder';
            S.onstatus = typeof opts.onstatus === 'function' ? opts.onstatus : function () {};
        },

        // A caller that ALREADY holds the camera (webcam-recorder's green room)
        // hands its source in instead of letting us open a second one — two
        // getUserMedia on the same device is a busy-camera failure on Windows.
        // `source` is a MediaStream, a <video> or a <canvas>; we never stop it.
        attachCam: function (source, opts) {
            opts = opts || {};
            var v = source;
            if (source && typeof source.getVideoTracks === 'function') {
                v = el('video');
                v.muted = true; v.playsInline = true; v.setAttribute('playsinline', ''); v.autoplay = true;
                v.srcObject = source;
                var p = v.play(); if (p && p.catch) p.catch(function () {});
            }
            if (!v) return false;
            S.camStream = null;        // not ours to stop
            // The stream itself is kept so the composite pump can read the
            // camera's frames directly (its own cadence, alive in a hidden tab).
            S.camSource = (source && typeof source.getVideoTracks === 'function') ? source : null;
            S.selfVideo = v;
            S.cam = true;
            if (opts.mirror !== undefined) S.mirror = !!opts.mirror;
            if (opts.corner) this.setCorner(opts.corner);
            if (opts.size) this.setBubbleSize(opts.size);
            return true;
        },
        detachCam: function () {
            S.cam = false;
            S.selfVideo = null;
            S.camSource = null;
        },

        camEnabled: function () { return S.cam; },
        drawEnabled: function () { return S.draw; },
        // «Активен» = есть что рисовать поверх кадра или есть другая форма
        // кадра. Рамка и вертикаль входят сюда наравне с камерой и
        // аннотациями: иначе блейд не стал бы звать beginComposite, и
        // выбранное молча не доехало бы до файла.
        isActive: function () { return S.cam || S.draw || !!FRAMES[S.frame] || S.out !== 'source'; },
        state: function () {
            return { shape: S.shape, pos: S.pos ? { x: S.pos.x, y: S.pos.y } : null, corner: S.corner, size: S.sizeFrac,
                layout: S.layout, out: S.out, swap: S.swap, camVisible: S.camVisible, mirror: S.mirror, frame: S.frame };
        },

        enableCam: function () {
            if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
                S.onstatus('nocam'); return Promise.resolve(false);
            }
            S.onstatus('loading');
            return acquireCam().then(function (stream) {
                S.camStream = stream;
                S.cam = true;
                var v = el('video');
                v.muted = true; v.playsInline = true; v.setAttribute('playsinline', ''); v.autoplay = true;
                v.srcObject = stream;
                var p = v.play(); if (p && p.catch) p.catch(function () {});
                S.selfVideo = v;
                showSelfView();
                S.onstatus('ready');
                tel('presenter_cam', { on: 1 });
                return true;
            }).catch(function (err) {
                S.cam = false;
                var name = err && err.name;
                S.onstatus(name === 'NotFoundError' ? 'nocam' : name === 'NotAllowedError' ? 'denied' : 'error');
                return false;
            });
        },
        disableCam: function () {
            S.cam = false;
            if (S.camStream) { try { S.camStream.getTracks().forEach(function (t) { t.stop(); }); } catch (_) {} S.camStream = null; }
            S.camSource = null;
            S.selfVideo = null;
            hideSelfView();
            tel('presenter_cam', { on: 0 });
        },
        // A corner is a preset: picking one forgets the dragged position.
        setCorner: function (c) { if (/^(br|bl|tr|tl)$/.test(c)) { S.corner = c; S.pos = null; touch(); } },
        setBubbleSize: function (f) { f = +f; if (f > 0) { S.sizeFrac = clamp(f, 0.12, 0.4); touch(); } },
        setMirror: function (b) { S.mirror = !!b; if (S.selfVideo) S.selfVideo.style.transform = b ? 'scaleX(-1)' : 'none'; touch(); },
        setShape: function (k) { S.shape = (k === 'square' || k === 'rect') ? k : 'circle'; touch(); },
        setPosition: function (x, y) {
            if (x === null || x === undefined) { S.pos = null; touch(); return; }
            x = +x; y = +y;
            if (!isFinite(x) || !isFinite(y)) return;
            S.pos = { x: clamp(x, 0, 1), y: clamp(y, 0, 1) };
            touch();
        },
        getPosition: function () { return S.pos ? { x: S.pos.x, y: S.pos.y } : null; },
        setLayout: function (l) { S.layout = l === 'split' ? 'split' : 'pip'; touch(); },
        setOut: function (o) { S.out = (o === '9:16' || o === '16:9' || o === '1:1') ? o : 'source'; touch(); },
        setSwap: function (b) { S.swap = !!b; touch(); },
        // «Область экрана»: доли кадра экрана или null. Ставится ДО
        // beginComposite — размер холста считается от области.
        setCrop: function (r) {
            var ok = r && [r.x, r.y, r.w, r.h].every(function (v) { return typeof v === 'number' && isFinite(v); })
                && r.w > 0 && r.h > 0 && r.x >= 0 && r.y >= 0 && r.x + r.w <= 1.0001 && r.y + r.h <= 1.0001;
            S.crop = ok ? { x: r.x, y: r.y, w: r.w, h: r.h } : null;
            touch();
        },
        getCrop: function () { return S.crop ? { x: S.crop.x, y: S.crop.y, w: S.crop.w, h: S.crop.h } : null; },
        setCamVisible: function (b) { S.camVisible = !!b; if (S.comp) S.comp.dirty = true; },

        // «Красивый кадр». Неизвестный ключ гасит рамку, а не роняет цикл:
        // настройка приезжает из localStorage и переживает переименования.
        setFrame: function (k) {
            S.frame = Object.prototype.hasOwnProperty.call(FRAMES, k) ? k : 'none';
            touch();
            tel('presenter_frame', { frame: S.frame });
        },
        frameEnabled: function () { return !!FRAMES[S.frame]; },

        setDrawEnabled: function (b) { S.draw = !!b; tel('presenter_draw', { on: b ? 1 : 0 }); },
        setColor: function (c) { if (c) S.color = c; },
        setBrush: function (px) { px = +px; if (px > 0) S.width = clamp(px, 2, 40); },
        clearDrawing: function () { S.strokes = []; S.current = null; },

        // Build the recorded composite. mainStream = the getDisplayMedia the
        // BLADE acquired (its gesture), or a camera (dual cameras). Returns the
        // stream to record, or null.
        //   opts.out         — output shape, default S.out
        //   opts.mainFit     — 'cover' for a camera main picture (fill the frame),
        //                      default 'contain' (a screen is shown whole)
        //   opts.keepSource  — do not stop the main tracks in endComposite
        //                      (the green room keeps its cameras between takes)
        //   opts.mainElement — a playing <video>/<canvas> as the main picture
        //                      («сначала сцена, потом лицо»); mainStream may be null
        //   opts.extraAudio  — audio tracks to carry in the output (the scene's sound)
        //   opts.force       — composite even with nothing to overlay
        beginComposite: function (screenStream, opts) {
            opts = opts || {};
            var mainEl = opts.mainElement || null;
            var hasStream = !!(screenStream && screenStream.getVideoTracks && screenStream.getVideoTracks().length);
            if (!hasStream && !mainEl) return null;
            if (opts.out) this.setOut(opts.out);
            // Рамка — третья причина композитить: без неё запись с одной лишь
            // рамкой уходила бы в кодировщик сырой, а настройка выглядела бы
            // применённой. Вертикаль — четвёртая.
            if (!opts.force && !this.isActive()) return null; // nothing to overlay
            var fps = opts.fps && opts.fps > 0 ? opts.fps : 30;
            var mainFit = opts.mainFit === 'cover' ? 'cover' : 'contain';

            var sv = null;
            if (hasStream) {
                sv = el('video');
                sv.muted = true; sv.playsInline = true; sv.setAttribute('playsinline', ''); sv.autoplay = true;
                sv.srcObject = new MediaStream(screenStream.getVideoTracks());
                var pp = sv.play(); if (pp && pp.catch) pp.catch(function () {});
            }

            var mw = 1280, mh = 720;
            if (hasStream) {
                var vset = {};
                try { vset = screenStream.getVideoTracks()[0].getSettings() || {}; } catch (e) {}
                mw = vset.width || 1280; mh = vset.height || 720;
                // область экрана: холст — её размера, а не всего экрана
                if (S.crop) { mw = Math.max(64, Math.round(mw * S.crop.w)); mh = Math.max(64, Math.round(mh * S.crop.h)); }
            } else {
                var md = srcDims(mainEl);
                if (md) { mw = md.w; mh = md.h; }
            }
            var size = outSize(mw, mh, S.out);
            var w = size.w, h = size.h;
            var canvas = el('canvas'); canvas.width = w; canvas.height = h;
            var ctx = canvas.getContext('2d');

            // Live preview + pointer drawing surface — shown ONLY when drawing
            // is on (the user must see the canvas to draw on it). For a
            // camera-bubble-only recording we DON'T surface it: the canvas
            // still captures fine off-DOM, and showing a live preview of the
            // screen you're recording is a hall-of-mirrors. captureStream()
            // works on a hidden/off-DOM canvas (air-draw always relied on that).
            var onpd = null, onpm = null, onpu = null;
            if (S.previewEl && S.draw) {
                S.previewEl.innerHTML = '';
                canvas.style.cssText = 'width:100%;height:100%;object-fit:contain;display:block;background:#05070d;touch-action:none;cursor:crosshair';
                S.previewEl.appendChild(canvas);
                // The stage ships class="hidden" (display:none). Clearing only
                // the `hidden` PROPERTY leaves the CLASS → still display:none →
                // a 0×0 canvas nothing can draw on. Remove both.
                S.previewEl.classList.remove('hidden');
                S.previewEl.hidden = false;
                var drawing = false;
                onpd = function (ev) {
                    if (!S.draw) return;
                    var n = ptrToNorm(canvas, w, h, ev);
                    if (!n) return;
                    drawing = true;
                    S.current = { color: S.color, width: S.width, pts: [n] };
                    if (S.comp) S.comp.dirty = true;
                    try { canvas.setPointerCapture(ev.pointerId); } catch (_) {}
                    ev.preventDefault();
                };
                onpm = function (ev) {
                    if (!drawing || !S.current) return;
                    var n = ptrToNorm(canvas, w, h, ev);
                    if (n) { S.current.pts.push(n); if (S.comp) S.comp.dirty = true; }
                    ev.preventDefault();
                };
                onpu = function (ev) {
                    if (!drawing) return;
                    drawing = false;
                    if (S.current && S.current.pts.length) S.strokes.push(S.current);
                    S.current = null;
                    if (S.comp) S.comp.dirty = true;
                    ev && ev.preventDefault();
                };
                canvas.addEventListener('pointerdown', onpd);
                canvas.addEventListener('pointermove', onpm);
                canvas.addEventListener('pointerup', onpu);
                canvas.addEventListener('pointercancel', onpu);
                canvas.addEventListener('pointerleave', onpu);
            }

            // ── Composite pump — MUST keep producing frames in a HIDDEN tab.
            // Feedback #1309 (Андрей, 2026-08-06): «делал запись 30 минут а
            // только один кадр начала» — 26.5 min recording, 200 frames total.
            // The original loop ran on requestAnimationFrame, and rAF STOPS
            // when the tab is hidden — the user switched away to do the thing
            // he was recording, the canvas froze on its last paint, and the
            // whole file showed one frame. (air-draw carried the same latent
            // defect; the draw toggle made compositing mainstream and exposed
            // it. Reproduced: 2 s visible + 10 s hidden → 60 frames ≈ the
            // visible seconds only.)
            //
            // Background-proof design: drive the loop from the SCREEN's own
            // frames via MediaStreamTrackProcessor — capture keeps delivering
            // in background tabs (the plain non-composite pipeline records
            // fine while hidden, which proves it) — draw each VideoFrame onto
            // the canvas and push it out with track.requestFrame() (manual
            // emission; captureStream(0) emits ONLY on requestFrame, no
            // compositor/rAF involvement). Non-MSTP browsers (Firefox MR
            // path) keep the old rAF loop — background compositing there
            // stays best-effort as before.
            //
            // ⚠️⚠️ The screen's frames are NOT a clock (2026-09-23, #7606,
            // /webcam-recorder «Виснет все. Не записывает»). Chromium delivers
            // a display-capture frame only when the captured surface CHANGES:
            // measured on real Chrome with a static window — 2 frames in 6 s,
            // a static tab — 0. The pump above painted the camera bubble only
            // on a screen frame, so a person talking to the camera over a
            // still document got a file with ONE frame in 32 s (the preview
            // froze the same way — that is the «виснет»). In prod, 8 of 83
            // screen-layout takes on /webcam-recorder came out under 2 fps.
            //
            // So the pump is now a TICKER at the target fps (a blob Worker's
            // clock — not throttled in a hidden tab, the same device the
            // engine's keepalive uses), and the sources only refresh state:
            // the screen reader keeps the LATEST screen frame, the camera
            // reader (when the bubble source is a MediaStream) keeps the
            // latest camera frame. A tick paints when something changed —
            // a new screen or camera frame, a stroke — so a still screen with
            // no camera costs nothing, and a live camera over a still screen
            // is painted at the camera's own cadence.
            var useMstp = typeof window.MediaStreamTrackProcessor === 'function';
            var stream = canvas.captureStream(useMstp ? 0 : fps);
            if (hasStream) screenStream.getAudioTracks().forEach(function (t) { try { stream.addTrack(t); } catch (e) {} });
            (opts.extraAudio || []).forEach(function (t) { try { if (t) stream.addTrack(t); } catch (e) {} });
            var capTrack = stream.getVideoTracks()[0];

            S.comp = { canvas: canvas, ctx: ctx, video: sv, mainEl: mainEl, raf: 0, stream: stream, screenStream: hasStream ? screenStream : null, keepSource: !!opts.keepSource, mainFit: mainFit, onpd: onpd, onpm: onpm, onpu: onpu, stopped: false, reader: null, camReader: null, camTrack: null, last: null, camFrame: null, dirty: false, ticker: null, tickerWorker: null, paints: 0, camFrames: 0, screenFrames: 0, region: { x: 0, y: 0, w: w, h: h, r: 0 }, drags: [] };

            var comp = S.comp;
            function mainNow() { return comp.last || sv || mainEl; }
            function paint() {
                try {
                    comp.region = paintComposite(ctx, w, h, mainNow(), comp.camFrame || S.selfVideo, mainFit) || comp.region;
                    if (capTrack && capTrack.requestFrame) capTrack.requestFrame();
                    comp.paints++;
                } catch (e) {}
                comp.dirty = false;
            }
            comp.paintNow = paint;
            if (useMstp) {
                // camera bubble from an element (processor canvas / <video>):
                // nothing tells us when it changed, so a live camera paints
                // on every tick — the same cost the old pump paid per screen
                // frame. A playing main ELEMENT («сцена, потом лицо») likewise.
                function tick() {
                    if (!S.comp || S.comp !== comp || comp.stopped) return;
                    if (comp.dirty || (S.cam && !comp.camReader) || (mainEl && !comp.reader)) paint();
                }
                var period = Math.max(16, Math.round(1000 / fps));
                try {
                    comp.tickerWorker = new Worker(URL.createObjectURL(new Blob(['setInterval(function(){postMessage(1)},' + period + ')'], { type: 'text/javascript' })));
                    comp.tickerWorker.onmessage = tick;
                } catch (_) { comp.ticker = setInterval(tick, period); }

                if (hasStream) {
                    // ⚠️ Cancelling a MediaStreamTrackProcessor's reader ENDS the track
                    // it reads. A source the caller keeps (keepSource: the green room's
                    // camera between takes) is therefore read through a clone.
                    var mainTrack = screenStream.getVideoTracks()[0];
                    var readTrack = comp.keepSource ? mainTrack.clone() : mainTrack;
                    if (comp.keepSource) comp.mainReadTrack = readTrack;
                    var proc = new MediaStreamTrackProcessor({ track: readTrack });
                    var reader = proc.readable.getReader();
                    comp.reader = reader;
                    (async function pumpScreen() {
                        while (S.comp === comp && !comp.stopped) {
                            var r;
                            try { r = await reader.read(); } catch (e) { break; }
                            if (r.done) break;
                            if (comp.stopped) { try { r.value.close(); } catch (e) {} break; }
                            try { if (comp.last) comp.last.close(); } catch (e) {}
                            comp.last = r.value;
                            comp.screenFrames++;
                            comp.dirty = true;
                        }
                    })();
                }

                // Camera frames straight from the track (a clone, so the
                // element that shows the self-view keeps its own sink).
                var camSrc = S.camStream || S.camSource;
                var camTrack0 = S.cam && camSrc && camSrc.getVideoTracks ? camSrc.getVideoTracks()[0] : null;
                if (camTrack0) {
                    try {
                        comp.camTrack = camTrack0.clone();
                        var cproc = new MediaStreamTrackProcessor({ track: comp.camTrack });
                        var creader = cproc.readable.getReader();
                        comp.camReader = creader;
                        (async function pumpCam() {
                            while (S.comp === comp && !comp.stopped) {
                                var r;
                                try { r = await creader.read(); } catch (e) { break; }
                                if (r.done) break;
                                if (comp.stopped) { try { r.value.close(); } catch (e) {} break; }
                                try { if (comp.camFrame) comp.camFrame.close(); } catch (e) {}
                                comp.camFrame = r.value;
                                comp.camFrames++;
                                comp.dirty = true;
                            }
                            // The camera went away mid-take (thermal cut, a call, another
                            // app took it): the bubble falls back to its element — dark
                            // box when that has no frame — and the take goes on.
                            try { if (comp.camFrame) comp.camFrame.close(); } catch (e) {}
                            comp.camFrame = null; comp.camReader = null;
                            if (S.comp === comp && !comp.stopped) tel('presenter_cam_lost', { camFrames: comp.camFrames });
                        })();
                    } catch (e) { comp.camReader = null; try { if (comp.camTrack) comp.camTrack.stop(); } catch (_) {} comp.camTrack = null; }
                }
            } else {
                (function frame() {
                    if (!S.comp || S.comp.stopped) return;
                    S.comp.raf = requestAnimationFrame(frame);
                    paint();
                })();
            }
            tel('presenter_composite', { cam: S.cam ? 1 : 0, draw: S.draw ? 1 : 0, frame: S.frame, w: w, h: h, pump: useMstp ? 'ticker' : 'raf', camTrack: (S.comp && S.comp.camReader) ? 1 : 0, shape: S.shape, layout: S.layout, out: S.out, dragged: S.pos ? 1 : 0, main: mainEl ? 'element' : 'stream' });
            return stream;
        },

        // Move the bubble on an element that shows THIS composite letterboxed
        // (a <video> of the composite stream, object-fit:contain):
        //   mouse / pen — press on the bubble and drag it;
        //   touch       — tap where the bubble should go. A FINGER drag is left
        //                 to the page: on a phone the preview is most of the
        //                 screen, and touch-action:none on it would take page
        //                 scrolling away (2026-10-20, «Две камеры»).
        //   opts.enabled() — false while a take runs (no accidental moves)
        attachDrag: function (target, opts) {
            opts = opts || {};
            if (!target) return function () {};
            var drag = null, upAt = 0;
            // the region of the picture as it is NOW — a pending change paints first
            function region() {
                if (!S.comp) return null;
                if (S.comp.dirty && typeof S.comp.paintNow === 'function') { try { S.comp.paintNow(); } catch (_) {} }
                return S.comp.region;
            }
            function pt(ev) { var c = S.comp && S.comp.canvas; return c ? ptrToNorm(target, c.width, c.height, ev) : null; }
            function movable() {
                if (!S.comp || !S.cam || !S.camVisible || S.layout === 'split') return false;
                try { return !opts.enabled || !!opts.enabled(); } catch (_) { return false; }
            }
            function told() { try { if (opts.onChange) opts.onChange(S.pos ? { x: S.pos.x, y: S.pos.y } : null); } catch (_) {} }
            function onDown(ev) {
                if (ev.pointerType === 'touch' || !movable()) return;
                var c = S.comp.canvas, R = region(), n = pt(ev);
                if (!n || !R) return;
                var px = n.x * c.width, py = n.y * c.height, b = bubbleGeom(R);
                if (px < b.x || px > b.x + b.w || py < b.y || py > b.y + b.h) return;
                drag = { ox: px - (b.x + b.w / 2), oy: py - (b.y + b.h / 2), R: R, W: c.width, H: c.height, id: ev.pointerId, moved: false };
                try { target.setPointerCapture(ev.pointerId); } catch (_) {}
                ev.preventDefault();
            }
            function onMove(ev) {
                if (!drag || ev.pointerId !== drag.id) return;
                var n = pt(ev); if (!n) return;
                var cx = n.x * drag.W - drag.ox, cy = n.y * drag.H - drag.oy;
                S.pos = { x: clamp((cx - drag.R.x) / drag.R.w, 0, 1), y: clamp((cy - drag.R.y) / drag.R.h, 0, 1) };
                drag.moved = true;
                if (S.comp) S.comp.dirty = true;
                ev.preventDefault();
            }
            function onUp(ev) {
                if (!drag || (ev && ev.pointerId !== drag.id)) return;
                var moved = drag.moved;
                drag = null;
                upAt = Date.now();   // the click that ends a press ON the bubble is not «put it here»
                if (moved) told();
            }
            // A tap (or a click off the bubble): the bubble's centre goes there.
            function onClick(ev) {
                if (upAt && Date.now() - upAt < 400) { upAt = 0; return; }
                if (!movable()) return;
                var R = region(), n = pt(ev), c = S.comp.canvas;
                if (!n || !R) return;
                S.pos = { x: clamp((n.x * c.width - R.x) / R.w, 0, 1), y: clamp((n.y * c.height - R.y) / R.h, 0, 1) };
                if (S.comp) S.comp.dirty = true;
                told();
            }
            target.addEventListener('pointerdown', onDown);
            target.addEventListener('pointermove', onMove);
            target.addEventListener('pointerup', onUp);
            target.addEventListener('pointercancel', onUp);
            target.addEventListener('click', onClick);
            return function detach() {
                target.removeEventListener('pointerdown', onDown);
                target.removeEventListener('pointermove', onMove);
                target.removeEventListener('pointerup', onUp);
                target.removeEventListener('pointercancel', onUp);
                target.removeEventListener('click', onClick);
            };
        },

        endComposite: function () {
            if (!S.comp) return;
            var c0 = S.comp;
            c0.stopped = true;
            // The composite's stats ride on endComposite so a frozen take
            // (screen still, camera live, few paints) is visible in the field.
            tel('presenter_composite_end', { paints: c0.paints || 0, screenFrames: c0.screenFrames || 0, camFrames: c0.camFrames || 0, camTrack: c0.camReader ? 1 : 0 });
            if (c0.tickerWorker) { try { c0.tickerWorker.terminate(); } catch (e) {} c0.tickerWorker = null; }
            if (c0.ticker) { clearInterval(c0.ticker); c0.ticker = null; }
            if (c0.reader) { try { c0.reader.cancel().catch(function () {}); } catch (e) {} }
            if (c0.camReader) { try { c0.camReader.cancel().catch(function () {}); } catch (e) {} }
            if (c0.camTrack) { try { c0.camTrack.stop(); } catch (e) {} c0.camTrack = null; }
            if (c0.mainReadTrack) { try { c0.mainReadTrack.stop(); } catch (e) {} c0.mainReadTrack = null; }
            try { if (c0.last) c0.last.close(); } catch (e) {} c0.last = null;
            try { if (c0.camFrame) c0.camFrame.close(); } catch (e) {} c0.camFrame = null;
            if (c0.raf) cancelAnimationFrame(c0.raf);
            var c = c0.canvas;
            if (c0.onpd) { try { c.removeEventListener('pointerdown', c0.onpd); c.removeEventListener('pointermove', c0.onpm); c.removeEventListener('pointerup', c0.onpu); c.removeEventListener('pointercancel', c0.onpu); c.removeEventListener('pointerleave', c0.onpu); } catch (_) {} }
            if (c0.video) { try { c0.video.pause(); c0.video.srcObject = null; } catch (e) {} }
            try { c0.stream.getVideoTracks().forEach(function (t) { t.stop(); }); } catch (e) {}
            // ⚠️ Отмена читателя MediaStreamTrackProcessor гасит сам трек: экран
            // после композита не переиспользуется (второй композит — свежий
            // getDisplayMedia). `keepSource` — для камер зелёной комнаты, их
            // гасит владелец.
            if (c0.screenStream && !c0.keepSource) { try { c0.screenStream.getVideoTracks().forEach(function (t) { t.stop(); }); } catch (e) {} }
            S.comp = null;
            if (S.previewEl) { S.previewEl.innerHTML = ''; S.previewEl.hidden = true; S.previewEl.classList.add('hidden'); }
            if (S.cam) showSelfView();
        },

        // test hooks
        _test: { state: function () { return S; }, outSize: outSize, bubbleGeom: function (R) { return bubbleGeom(R); } }
    };
})();
